import React, { createContext, useContext, useState, useEffect } from 'react';
import { licenseAPI } from '../api';

const LicenseContext = createContext(null);

export const useLicense = () => useContext(LicenseContext);

export const LicenseProvider = ({ children }) => {
  const [license, setLicense] = useState(null);
  const [modules, setModules] = useState({});
  const [loading, setLoading] = useState(true);

  // Load license on mount
  useEffect(() => {
    loadLicense();
    loadModules();
  }, []);

  const loadLicense = async () => {
    try {
      const res = await licenseAPI.getCurrent();
      setLicense(res.data);
    } catch (err) {
      console.error('Error loading license:', err);
      setLicense({ has_license: false, modules: [], is_expired: true });
    } finally {
      setLoading(false);
    }
  };

  const loadModules = async () => {
    try {
      const res = await licenseAPI.getModules();
      setModules(res.data);
    } catch (err) {
      console.error('Error loading modules:', err);
    }
  };

  const activateLicense = async (licenseKey) => {
    const res = await licenseAPI.activate(licenseKey);
    await loadLicense();
    return res.data;
  };

  // Check if a specific feature/view is accessible
  const hasAccess = (feature) => {
    if (!license || !license.has_license || license.is_expired) {
      return false;
    }
    
    // Check which module contains this feature
    for (const [moduleKey, moduleInfo] of Object.entries(modules)) {
      if (moduleInfo.features?.includes(feature)) {
        return license.modules.includes(moduleKey);
      }
    }
    
    // Dashboard is always accessible if any module is active
    if (feature === 'dashboard') {
      return license.modules.length > 0;
    }
    
    return false;
  };

  // Check if a module is active
  const hasModule = (moduleKey) => {
    if (!license || !license.has_license || license.is_expired) {
      return false;
    }
    return license.modules.includes(moduleKey);
  };

  // Get accessible navigation items
  const getAccessibleNavItems = (navItems) => {
    if (!license || !license.has_license || license.is_expired) {
      return [];
    }
    
    return navItems.filter(item => {
      // Module-based check
      if (item.module) {
        return license.modules.includes(item.module);
      }
      // Feature-based check
      return hasAccess(item.key);
    });
  };

  const value = {
    license,
    modules,
    loading,
    hasAccess,
    hasModule,
    getAccessibleNavItems,
    activateLicense,
    refreshLicense: loadLicense,
  };

  return (
    <LicenseContext.Provider value={value}>
      {children}
    </LicenseContext.Provider>
  );
};

export default LicenseContext;
